import { toast } from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {CreateFormat, CreateHidRecord} from '../../../../../reduxToolkit/HID/HidApi';
import { t } from 'i18next';
import routes from '../../../../../Routes/routes';
import { errorMsg } from "../../../../../Apis/functions";
import React, {useEffect, useState} from "react";
import {GetAllCardFormats} from "../../../../../reduxToolkit/Cards/CardsApi";
import HandleChange from "../../../../../Helpers/HandleChange";
import {FORMAT_DATA} from "../../Formats/Model/Models";

/**
 * @author Karen Yunuen Pozos Arias
 * @position Full Stack Developer - IBL
 * @contact kpozos@ibl.mx
 *
 * @description
 * CreateControllerApp handles the logic to submit a new controller record.
 * Validates the controller data and IP address before dispatching the creation API.
 *
 * @param {object} controller - Controller model with validation and data conversion methods.
 * @param {Function} setEmptyFields - Sets the state to indicate empty required fields.
 * @param {boolean} isValidIp - Flag indicating if the entered IP address is valid.
 *
 * @returns {object} - An object containing the handleSubmit function.
 */
const CreateControllerApp = ({
                                 controller,
                                 setEmptyFields,
                                 isValidIp
                             }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { getAllCardFormats } = useSelector((state) => state.CardsSlice);
    const [format, setFormat] = useState({...FORMAT_DATA});
    const [isValidFacilityCode, setIsValidFacilityCode] = useState(true);
    const { handleChange } = HandleChange({ model: format, setModel: setFormat });

    useEffect(() => {
        dispatch(GetAllCardFormats()); // Fetch all card formats
    }, []);

    /**
     * Submits a new controller record.
     * Validates required fields and IP format before dispatching the API.
     * On success, shows a success toast and navigates to the controller list page.
     * On failure, displays an error message with relevant details.
     */
    const handleSubmit = () => {
        const isControllerValid = controller.isValid();
        const isFormatValid = format.isValidFromController();

        if (!isControllerValid || !isFormatValid || !isValidIp || !isValidFacilityCode) {
            // The card format fields are required too, so a missing facility code or
            // format must flag the empty fields as well
            if (!isControllerValid || !isFormatValid) setEmptyFields(true);
            return;
        }

        const body = controller.toAddEdit();

        dispatch(CreateHidRecord(body)).then(({ payload: { data: { data, code, success } } }) => {
            if (success === true) {
                createCardFormat(data?.id);
            } else {
                errorMsg(code, { offset: controller.offset, scpId: controller.scpId }, { bold: <strong /> });
            }
        });
    };

    const createCardFormat = (controllerId) => {
        const body = format.toAddEdit(controllerId);
        dispatch(CreateFormat(body)).then(({ payload: { data: { data, success, code }, } }) => {
                if (success === true) {
                    toast.success(t('has_been_created_successfully'));
                    navigate(routes.controller.list);
                } else errorMsg(code);
            },
        );
    }

    const handleClearFormat = () => {
        setFormat({...FORMAT_DATA});
    }

    return {
        handleSubmit,
        handleClearFormat,
        getAllCardFormats,
        format,
        isValidFacilityCode,
        setIsValidFacilityCode,
        handleChangeFormat:handleChange
    };
};

export default CreateControllerApp;
